import React, { createContext, ReactNode, useCallback, useContext, useState, useRef } from 'react';
import noop from 'lodash/noop';

import { Genre, Route } from 'types';

type TRoute = {
  name: Route;
  param?: string | Genre;
};

interface IRouterContext {
  currentRoute: TRoute;
  history: TRoute[];
  next: (route: TRoute) => void;
  back: () => void;
}

interface IRouterContextProvider {
  children?: ReactNode;
}

const DEFAULT_ROUTE: TRoute = {
  name: Route.home,
}

const RouterContext = createContext<IRouterContext>({
  currentRoute: DEFAULT_ROUTE,
  history: [],
  next: noop,
  back: noop,
});

export const RouterContextProvider: React.FC<IRouterContextProvider> = ({ children }) => {
  const history = useRef<TRoute[]>([]);
  const [currentRoute, setCurrentRoute] = useState<TRoute>(DEFAULT_ROUTE);

  const handleNext = useCallback((nextRoute: TRoute) => {
    setCurrentRoute({
      name: nextRoute.name,
      param: nextRoute.param,
    });

    // Save in the history where we were before the next page.
    history.current.push(currentRoute);
  }, [currentRoute]);

  const handleBack = useCallback(() => {
    if (!history.current.length) {
      return;
    }

    // Take the last route from the history and shift the history array.
    const prevRoute = history.current.pop();

    setCurrentRoute(prevRoute as TRoute);
  }, []);

  return (
    <RouterContext.Provider value={{ currentRoute, history: history.current, next: handleNext, back: handleBack }}>
      {children}
    </RouterContext.Provider>
  );
};

export const useRouterContext = () => {
  return useContext(RouterContext);
};
